import axios from 'axios';
import type {
  PortalDashboard,
  PortalInvoice,
  PortalProject,
  PortalReport,
  PortalTicket,
  PortalTicketDetail,
  PortalTokenResponse,
  PortalConversation,
  PortalMessage,
  PortalNotification,
  PortalClient,
} from '@/types/portal.types';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL ?? 'http://localhost:8007';
export const PORTAL_WS_URL = PORTAL_URL.replace(/^http/, 'ws');
export const TOKEN_KEY = 'portal_token';
export const REFRESH_KEY = 'portal_refresh_token';

// ── snake_case → camelCase transformer ────────────────────────

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

function deepTransform(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(deepTransform);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        snakeToCamel(k),
        deepTransform(v),
      ])
    );
  }
  return obj;
}

// ── Axios instance ─────────────────────────────────────────────

const client = axios.create({ baseURL: PORTAL_URL });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use((response) => {
  response.data = deepTransform(response.data);
  return response;
});

// ── API methods ────────────────────────────────────────────────

export const portalApi = {
  // Auth
  login: async (email: string, password: string): Promise<PortalTokenResponse> => {
    const { data } = await client.post('/api/portal/auth/login', { email, password });
    return data as PortalTokenResponse;
  },

  register: async (
    invitationToken: string,
    firstName: string,
    lastName: string,
    password: string,
    language = 'fr'
  ): Promise<PortalTokenResponse> => {
    const { data } = await client.post('/api/portal/auth/register', {
      invitation_token: invitationToken,
      first_name: firstName,
      last_name: lastName,
      password,
      language,
    });
    return data as PortalTokenResponse;
  },

  googleAuth: async (code: string, redirectUri: string): Promise<PortalTokenResponse> => {
    const { data } = await client.post('/api/portal/auth/google', { code, redirect_uri: redirectUri });
    return data as PortalTokenResponse;
  },

  refreshToken: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const { data } = await client.post('/api/portal/auth/refresh', { refresh_token: refreshToken });
    return data as { accessToken: string };
  },

  logout: async (): Promise<void> => {
    await client.post('/api/portal/auth/logout').catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },

  forgotPassword: async (email: string): Promise<void> => {
    await client.post('/api/portal/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await client.post('/api/portal/auth/reset-password', { token, new_password: newPassword });
  },

  getMe: async (): Promise<PortalClient> => {
    const { data } = await client.get('/api/portal/auth/me');
    return data as PortalClient;
  },

  // Dashboard
  getDashboard: async (): Promise<PortalDashboard> => {
    const { data } = await client.get('/api/portal/dashboard');
    return data as PortalDashboard;
  },

  // Projets
  getProjects: async (): Promise<PortalProject[]> => {
    const { data } = await client.get('/api/portal/projects');
    return data as PortalProject[];
  },

  getProject: async (id: string): Promise<PortalProject> => {
    const { data } = await client.get(`/api/portal/projects/${id}`);
    return data as PortalProject;
  },

  getMilestones: async (id: string): Promise<unknown[]> => {
    const { data } = await client.get(`/api/portal/projects/${id}/milestones`);
    return data as unknown[];
  },

  getPendingValidation: async (): Promise<unknown[]> => {
    const { data } = await client.get('/api/portal/studio/pending');
    return data as unknown[];
  },

  approveContent: async (id: string): Promise<void> => {
    await client.post(`/api/portal/studio/${id}/approve`);
  },

  requestChanges: async (id: string, comment: string): Promise<void> => {
    await client.post(`/api/portal/studio/${id}/request-changes`, { comment });
  },

  // Factures
  getInvoices: async (): Promise<PortalInvoice[]> => {
    const { data } = await client.get('/api/portal/invoices');
    return data as PortalInvoice[];
  },

  downloadInvoicePdf: (id: string): void => {
    window.open(`${PORTAL_URL}/api/portal/invoices/${id}/pdf`, '_blank');
  },

  declarePayment: async (id: string): Promise<void> => {
    await client.post(`/api/portal/invoices/${id}/declare-payment`);
  },

  // Tickets
  getTickets: async (status?: string): Promise<PortalTicket[]> => {
    const { data } = await client.get('/api/portal/tickets', { params: status ? { status } : {} });
    return data as PortalTicket[];
  },

  createTicket: async (payload: {
    subject: string;
    description: string;
    category: string;
    priority?: string;
  }): Promise<PortalTicket> => {
    const { data } = await client.post('/api/portal/tickets', payload);
    return data as PortalTicket;
  },

  getTicket: async (id: string): Promise<PortalTicketDetail> => {
    const { data } = await client.get(`/api/portal/tickets/${id}`);
    return data as PortalTicketDetail;
  },

  addTicketMessage: async (id: string, content: string): Promise<void> => {
    await client.post(`/api/portal/tickets/${id}/messages`, { content });
  },

  closeTicket: async (id: string): Promise<PortalTicket> => {
    const { data } = await client.post(`/api/portal/tickets/${id}/close`);
    return data as PortalTicket;
  },

  rateTicket: async (id: string, score: number, comment?: string): Promise<PortalTicket> => {
    const { data } = await client.post(`/api/portal/tickets/${id}/rate`, { score, comment });
    return data as PortalTicket;
  },

  // Conversations
  getConversations: async (): Promise<PortalConversation[]> => {
    const { data } = await client.get('/api/portal/conversations');
    return data as PortalConversation[];
  },

  createConversation: async (title: string, projectId?: string): Promise<PortalConversation> => {
    const { data } = await client.post('/api/portal/conversations', {
      title,
      pulse_project_id: projectId,
    });
    return data as PortalConversation;
  },

  getMessages: async (id: string, page = 1): Promise<{ messages: PortalMessage[]; total: number; page: number }> => {
    const { data } = await client.get(`/api/portal/conversations/${id}/messages`, {
      params: { page, limit: 30 },
    });
    return data as { messages: PortalMessage[]; total: number; page: number };
  },

  sendMessage: async (id: string, content: string, replyToId?: string): Promise<PortalMessage> => {
    const { data } = await client.post(`/api/portal/conversations/${id}/messages`, {
      content,
      reply_to_id: replyToId,
    });
    return data as PortalMessage;
  },

  // Rapports
  getReports: async (): Promise<PortalReport[]> => {
    const { data } = await client.get('/api/portal/reports');
    return data as PortalReport[];
  },

  downloadReport: (id: string): void => {
    const token = localStorage.getItem(TOKEN_KEY);
    window.open(`${PORTAL_URL}/api/portal/reports/${id}/download?token=${token}`, '_blank');
  },

  // Demandes
  getRequests: async (): Promise<unknown[]> => {
    const { data } = await client.get('/api/portal/requests');
    return data as unknown[];
  },

  createRequest: async (payload: { type: string; title: string; description: string }): Promise<unknown> => {
    const { data } = await client.post('/api/portal/requests', payload);
    return data;
  },

  getRequest: async (id: string): Promise<unknown> => {
    const { data } = await client.get(`/api/portal/requests/${id}`);
    return data;
  },

  approveDelivery: async (id: string): Promise<void> => {
    await client.post(`/api/portal/requests/${id}/approve-delivery`);
  },

  // Profil
  getProfile: async (): Promise<PortalClient> => {
    const { data } = await client.get('/api/portal/profile');
    return data as PortalClient;
  },

  updateProfile: async (payload: Partial<{
    firstName: string;
    lastName: string;
    phone: string;
    language: string;
  }>): Promise<PortalClient> => {
    const { data } = await client.put('/api/portal/profile', {
      first_name: payload.firstName,
      last_name: payload.lastName,
      phone: payload.phone,
      language: payload.language,
    });
    return data as PortalClient;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await client.post('/api/portal/profile/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  // Notifications
  getNotifications: async (): Promise<PortalNotification[]> => {
    const { data } = await client.get('/api/portal/notifications');
    return data as PortalNotification[];
  },

  markAllRead: async (): Promise<void> => {
    await client.post('/api/portal/notifications/read-all');
  },
};
